// Free, no-key APIs:
// - freeipapi.com: IP -> country detection (60 req/min per IP, no key)
// - @fawazahmed0/currency-api: USD-based exchange rates, no key, no rate limit.
//   Served from two independent free hosts; if the primary CDN is unreachable
//   (blocked, down, rate-limited) we retry against the Cloudflare Pages mirror.
import { DEFAULT_CURRENCY } from './countryMap';

// Use the "free." subdomain directly — freeipapi.com/api/json/ 302-redirects
// here, and that cross-origin redirect trips CORS in browsers even though
// curl (which doesn't enforce CORS) follows it fine.
const COUNTRY_ENDPOINT = 'https://free.freeipapi.com/api/json/';
const RATES_ENDPOINTS = [
  'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
  'https://latest.currency-api.pages.dev/v1/currencies/usd.json',
];

const COUNTRY_CACHE_KEY = 'bv_geo_country_v1';
const RATES_CACHE_KEY = 'bv_fx_rates_v1';
const RATES_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours; the API itself refreshes daily

function readSession(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable (e.g. private mode) — safe to ignore
  }
}

function readLocal(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable — safe to ignore
  }
}

// Returns { countryCode, currencyCode } — currencyCode comes straight from the
// IP lookup when available, so it stays accurate even for countries missing
// from the static country->currency fallback table.
export async function detectCountryAndCurrency() {
  const cached = readSession(COUNTRY_CACHE_KEY);
  if (cached && cached.countryCode) return cached;

  try {
    const res = await fetch(COUNTRY_ENDPOINT);
    if (!res.ok) throw new Error(`geo lookup failed: ${res.status}`);
    const data = await res.json();
    const countryCode = data && data.countryCode ? data.countryCode : null;
    const currencyCode =
      data && Array.isArray(data.currencies) && data.currencies.length > 0
        ? data.currencies[0]
        : null;
    if (countryCode) {
      const result = { countryCode, currencyCode };
      writeSession(COUNTRY_CACHE_KEY, result);
      return result;
    }
  } catch {
    // fall through — caller defaults to USD/English
  }
  return { countryCode: null, currencyCode: null };
}

export async function fetchExchangeRates() {
  const cached = readLocal(RATES_CACHE_KEY);
  if (cached && cached.rates && Date.now() - cached.timestamp < RATES_TTL_MS) {
    return cached.rates;
  }

  for (const endpoint of RATES_ENDPOINTS) {
    try {
      const res = await fetch(endpoint);
      if (!res.ok) throw new Error(`rates lookup failed: ${res.status}`);
      const data = await res.json();
      if (data && data.usd) {
        // Normalize lowercase currency codes (e.g. "eur") to uppercase ISO codes ("EUR").
        const rates = {};
        Object.keys(data.usd).forEach((code) => {
          rates[code.toUpperCase()] = data.usd[code];
        });
        writeLocal(RATES_CACHE_KEY, { rates, timestamp: Date.now() });
        return rates;
      }
    } catch {
      // try the next endpoint
    }
  }

  return cached && cached.rates ? cached.rates : { [DEFAULT_CURRENCY]: 1 };
}
